import type { Knowledge } from "api/api.types";
import { InfinityLoader } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import type { TrackPage } from "domains/analytics/types";
import { InteractiveDataListItem } from "domains/knowledgeData/components/InteractiveDataListItem";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { useMedia } from "domains/viewport/useMedia";

type InteractiveDataListProps = {
  interactiveData: Knowledge[];
  emptyMessage?: string;
  hasNextPage?: boolean;
  isPending?: boolean;
  lockTiles?: boolean;
  selectedId?: string;
  showEmptyMessage?: boolean;
  trackPage: TrackPage;
  getToLink: (id: string) => string;
  loadNextPage?: () => void;
};

const LATEST_KNOWLEDGE_PUBLIC_LIMIT = 4;

export const InteractiveDataList = ({
  interactiveData,
  hasNextPage = false,
  isPending = false,
  selectedId,
  trackPage,
  lockTiles,
  emptyMessage,
  showEmptyMessage = true,
  loadNextPage,
  getToLink,
}: InteractiveDataListProps) => {
  const isList = useMedia({ small: true, medium: true }, false);
  const selectedTopics = useSelector(getTopicsFilterSelectedIds);
  const translate = useTranslate();

  if (showEmptyMessage && interactiveData.length === 0 && !isPending) {
    if (emptyMessage) {
      return <p>{emptyMessage}</p>;
    }
    return (
      <p>{translate(selectedTopics.length === 0 ? "app.monitor.empty_message" : "app.monitor.empty_message_filter")}</p>
    );
  }

  return (
    <InfinityLoader isPending={isPending} hasNextPage={hasNextPage} loadNextPage={loadNextPage}>
      {interactiveData.map((item, index) => (
        <InteractiveDataListItem
          key={item.id}
          title={item.title}
          imageUrl={item.images?.x_small}
          link={getToLink(item.id)}
          source={item.source}
          timestamp={item.time}
          isSelected={!isList && selectedId === item.id}
          isLocked={lockTiles && index >= LATEST_KNOWLEDGE_PUBLIC_LIMIT}
          trackPage={trackPage}
          isFirstLocked={index === LATEST_KNOWLEDGE_PUBLIC_LIMIT}
        />
      ))}
    </InfinityLoader>
  );
};
